import React, { useState, useMemo } from 'react';
import CreditCard from './CreditCard';
import DirectDebit from './DirectDebit';
import PaymentMethodSelector  from './PaymentMethodSelector';
import CardNumberPreview from './CreditCard/cardNumberPreview';
import DirectDebitPreview from './DirectDebit/directDebitPreview';
import Payout from './Payout';
import DeductionFromPayoutsPreview from './Payout/DeductionFromPayoutsPreview';
import SuPaymentMethod from './SuPaymentMethod';
import SuPaymentMethodPreview from './SuPaymentMethod/SuPaymentMethodPreview';
import SuBusinessAccount from './SuBusinessAccount';
import SuBusinessAccountPreview from './SuBusinessAccount/SuBusinessAccountPreview';
import { PAYMENT_METHODS } from '../../constants';
import { getDefaultPaymentMethodType } from './defaultPaymentMethodType';
import { shouldShowCardZipCode } from '../../utils/zip-code';

const PaymentMethod = ({
	availablePaymentMethods,
	businessAccountEnabled,
	showCardZipCode,
	countryCode,
	onChange,
	type: initialPaymentMethodType,
	...rest
}) => {
	const { directDebit, businessAccount, suPayout, suPaymentMethods, suBusinessAccount } = availablePaymentMethods;
	const [paymentMethodType, setPaymentMethodType] = useState(() => getDefaultPaymentMethodType(
		availablePaymentMethods,
		initialPaymentMethodType,
	));
	const handleChange = (paymentMethod) => onChange({
		paymentMethodType,
		paymentMethod,
	});

	const showZipCode = shouldShowCardZipCode(showCardZipCode, countryCode);

	const component = useMemo(() => {
		switch (paymentMethodType) {
			case PAYMENT_METHODS.suPayout:
				handleChange({ type: PAYMENT_METHODS.suPayout });
				return <Payout />;
			case PAYMENT_METHODS.suPaymentMethods:
				handleChange({ type: PAYMENT_METHODS.suPaymentMethods });
				return <SuPaymentMethod />;
			case PAYMENT_METHODS.suBusinessAccount:
				handleChange({ type: PAYMENT_METHODS.suBusinessAccount });
				return <SuBusinessAccount />;
			case PAYMENT_METHODS.creditCard:
				return <CreditCard onChange={handleChange} showZipCode={showZipCode} {...rest} />;
			case PAYMENT_METHODS.directDebit:
				return  <DirectDebit onChange={handleChange} {...rest} />;
			case PAYMENT_METHODS.businessAccount:
				handleChange({ type: PAYMENT_METHODS.businessAccount });
				return <div data-testid="business-account"></div>;
			default:
				return null;
		}
	}, [paymentMethodType]);


	const multiplePaymentMethods = !!(directDebit || businessAccount || suPayout || suPaymentMethods || suBusinessAccount);

	return <>
		{multiplePaymentMethods && <PaymentMethodSelector
			availablePaymentMethods={availablePaymentMethods}
			businessAccountEnabled={businessAccountEnabled}
			initialValue={paymentMethodType}
			onChange={setPaymentMethodType}
		/>}
		{component}
	</>;
};

export const PaymentMethodPreview = ({ type, paymentDetails }) => {
	if (type === PAYMENT_METHODS.creditCard) {
		const { last4, type } = paymentDetails;

		return <CardNumberPreview last4={last4} type={type}/>;
	} else if (type === PAYMENT_METHODS.directDebit) {
		const { maskedIban, mandateReference  } = paymentDetails;

		return <DirectDebitPreview maskedIban={maskedIban} mandateReference={mandateReference}/>;
	} else if (type === PAYMENT_METHODS.businessAccount) {
		const { maskedIban, mandateReference  } = paymentDetails;

		return <DirectDebitPreview maskedIban={maskedIban} mandateReference={mandateReference}/>;
	} else if (type === PAYMENT_METHODS.suPayout) {
		return <DeductionFromPayoutsPreview />;
	} else if (type === PAYMENT_METHODS.suPaymentMethods) {
		return <SuPaymentMethodPreview />;
	} else if (type === PAYMENT_METHODS.suBusinessAccount) {
		return <SuBusinessAccountPreview />;
	}

	return null;
};

export default PaymentMethod;
